
import React, { useState } from 'react';
import { CopyrightBundleLayout } from '../../components/CopyrightBundleLayout';
import { Link, CheckCircle, ContentCopy, Policy, QrCode2 } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

export const CopyrightBlockchainScreen: React.FC = () => {
  const { t } = useLanguage();
  const [copiedHash, setCopiedHash] = useState(false);
  const [copiedSig, setCopiedSig] = useState(false);

  const copyToClipboard = (text: string, setCopied: (v: boolean) => void) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <CopyrightBundleLayout>
        <div className="relative w-full max-w-4xl bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" style={{ background: 'radial-gradient(circle at 100% 0%, #F8FAFC 0%, #FFFFFF 100%)' }}>
            <div className="p-8 border-b border-slate-100 flex justify-between items-start">
                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <Link className="text-primary" />
                        <h2 className="text-xl font-bold text-slate-800 tracking-tight">{t('copyright.blockchain.title')}</h2>
                    </div>
                    <p className="text-xs text-slate-400 font-medium uppercase tracking-widest">{t('copyright.blockchain.subtitle')}</p>
                </div>
                <div className="flex flex-col items-end gap-3">
                    <div className="px-4 py-2 bg-emerald-50 border border-emerald-100 rounded-lg flex items-center gap-2">
                        <CheckCircle className="text-emerald-600" style={{ fontSize: 16 }} />
                        <span className="text-[11px] font-bold text-emerald-700 tracking-wide uppercase">{t('copyright.blockchain.verified')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-400">
                        <span className="text-[10px] font-bold uppercase tracking-tighter">{t('copyright.blockchain.network')}</span>
                        <span className="text-[10px] font-bold text-slate-600">{t('copyright.blockchain.networkVal')}</span>
                    </div>
                </div>
            </div>

            <div className="p-8 space-y-8">
                <div className="grid grid-cols-2 gap-x-12 gap-y-8">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.blockchain.txHash')}</label>
                        <div className="flex items-center gap-2 group">
                            <code className="text-xs font-mono text-primary bg-blue-50/50 px-2 py-1 rounded border border-blue-100 break-all leading-relaxed">0x8f4e2d3a9b1c7e5f6a4d2b8c9e0f1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f</code>
                            <button
                                onClick={() => copyToClipboard('0x8f4e2d3a9b1c7e5f6a4d2b8c9e0f1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f', setCopiedHash)}
                                className="text-slate-300 hover:text-primary transition-colors relative"
                            >
                                <ContentCopy fontSize="small" style={{ fontSize: 16 }} />
                                {copiedHash && <span className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[9px] px-2 py-0.5 rounded">Copied!</span>}
                            </button>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.blockchain.signature')}</label>
                        <div className="flex items-center gap-2">
                            <code className="text-xs font-mono text-slate-600 bg-slate-50/50 px-2 py-1 rounded border border-slate-100 break-all leading-relaxed">sig_0x77d8c2...1f8e9a2b</code>
                            <button
                                onClick={() => copyToClipboard('sig_0x77d8c2...1f8e9a2b', setCopiedSig)}
                                className="text-slate-300 hover:text-primary transition-colors relative"
                            >
                                <Policy fontSize="small" style={{ fontSize: 16 }} />
                                {copiedSig && <span className="absolute -top-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-[9px] px-2 py-0.5 rounded">Copied!</span>}
                            </button>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.blockchain.block')}</label>
                        <p className="text-sm font-mono font-bold text-slate-800">184,293,401</p>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.blockchain.timestamp')}</label>
                        <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-slate-800 tracking-tight">Oct 24, 2023 • 14:22:18.042 UTC</p>
                        </div>
                    </div>
                </div>

                <div className="mt-12 pt-8 border-t border-slate-100 flex justify-between items-end">
                    <div className="max-w-md">
                        <h4 className="text-[10px] font-bold text-slate-800 uppercase tracking-widest mb-2">{t('copyright.blockchain.legalNoteTitle')}</h4>
                        <p className="text-[11px] leading-relaxed text-slate-500">{t('copyright.blockchain.legalNoteDesc')}</p>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                        <div className="p-2 bg-white border border-slate-200 rounded-lg shadow-sm">
                            <div className="w-20 h-20 bg-slate-50 flex items-center justify-center relative">
                                <QrCode2 className="text-slate-400" fontSize="large" />
                            </div>
                        </div>
                        <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.blockchain.scan')}</span>
                    </div>
                </div>
            </div>
        </div>

        <div className="w-full max-w-4xl mt-8 flex justify-between items-end">
            <div className="space-y-1">
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.blockchain.verifStatus')}</span>
                <div className="flex items-center gap-3">
                    <span className="text-xs font-mono font-bold text-slate-600">{t('copyright.blockchain.protocol')}</span>
                    <span className="px-2 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100">{t('copyright.blockchain.confirmed')}</span>
                </div>
            </div>
            <div className="flex gap-4">
                <div className="text-right">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.blockchain.confirmations')}</span>
                    <p className="text-xs font-mono font-bold text-slate-800">12,842</p>
                </div>
                <div className="text-right">
                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{t('copyright.blockchain.integrity')}</span>
                    <p className="text-xs font-mono font-bold text-slate-800">{t('copyright.blockchain.secure')}</p>
                </div>
            </div>
        </div>
    </CopyrightBundleLayout>
  );
};
